import React, { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createWSClient, wsLink } from '@trpc/client';
import { SuperJSON } from 'superjson';
import { singletonHook } from 'react-singleton-hook';
import { trpc } from '@duke-hq/svc-support-web/trpc';

import { type ChatActions, ChatContext } from './Context';

function getProtocols() {
  let httpProtocol = 'http://';
  let wsProtocol = 'ws://';
  if (window.location.protocol === 'https:') {
    httpProtocol = 'https://';
    wsProtocol = 'wss://';
  }
  return { httpProtocol, wsProtocol };
}

type init = {
  loading: boolean;
  queryClient?: QueryClient;
  trpcClient?: ReturnType<typeof trpc.createClient>;
};

const useSupportTrpcClientImpl = () => {
  const [data, setData] = useState<init>({
    loading: false,
    queryClient: undefined,
    trpcClient: undefined,
  });

  useEffect(() => {
    const url = `${getProtocols().wsProtocol}${window.location.host}/api/support`;

    setData((data) => ({
      ...data,
      loading: false,
      queryClient: new QueryClient(),
      trpcClient: trpc.createClient({
        links: [
          wsLink({
            client: createWSClient({
              url,
              retryDelayMs: (attemptIndex: number) => {
                return attemptIndex === 0 ? 0 : Math.min(1000 * 2 ** attemptIndex, 30000);
              },
            }),
            transformer: SuperJSON,
          }),
        ],
      }),
    }));
  }, []);

  return data;
};

type UseSupportTrpc = ReturnType<typeof useSupportTrpcClientImpl>;

const useSupportTrpcClient = singletonHook<UseSupportTrpc>(
  { loading: true, queryClient: undefined, trpcClient: undefined },
  useSupportTrpcClientImpl,
);

// This component provides the trpc client for the support chat service.
//
// singletonHook cannot be rendered server-side we need to wrap up the
// client setup into a component that is lazy loaded only on the client side.
function ClientOnlyChatProvider({
  children,
  chatActions,
}: {
  chatActions: ChatActions;
  children?: React.ReactNode;
}) {
  const { loading, queryClient, trpcClient } = useSupportTrpcClient();

  if (loading) {
    return <div>Loading</div>;
  }

  if (!queryClient || !trpcClient) {
    // an error occurred
    return <div>Failed to load support chat</div>;
  }

  return (
    <ChatContext.Provider value={chatActions}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </trpc.Provider>
    </ChatContext.Provider>
  );
}

export default ClientOnlyChatProvider;
